import type { Scene, SceneNodeId, LayerNode } from '../../scene/sceneState.js';
import { returnToScene, switchToComponent, type SceneTreeActionDeps } from './actions.js';
import { subscribeEditorTargetSynced } from '../../editor/editorEvents.js';
import { renderPropertiesInspector, type SceneTreePropertiesDeps } from './properties.js';
import {
  findInitialSelectionForActiveComponent,
  getActiveContainerId,
  getActiveScene,
} from './queries.js';
import {
  isSceneFavoriteTargetSaved,
  isSceneFavoriteTargetValid,
  prepareViewportForSceneFavoriteNavigation,
  sceneFavoriteTargetForNode,
  sceneFavoriteTargetForVirtual,
  toggleSceneFavorite,
} from '../../state/sceneFavorites.js';
import { getOwnerSceneFor } from '../../state/viewportTargets.js';
import {
  type SceneTreeSelectionController,
} from './selection.js';
import { resolveViewportClickPolicy } from './viewportClickPolicy.js';
import { renderSceneTreeToolbar } from './treeToolbar.js';
import type { AppState, SceneFavoriteTarget } from '../../state/appState.js';
import type { ViewportMode, ViewportTarget } from '../../state/appState.js';

export interface SceneTreeRendererDeps
  extends SceneTreePropertiesDeps,
    Omit<SceneTreeActionDeps, 'appState' | 'commands'> {
  appState: AppState;
  commands: SceneTreePropertiesDeps['commands'] & SceneTreeActionDeps['commands'];
  selection: SceneTreeSelectionController;
  onFavoritesChanged?: () => void;
  syncViewportToolbar?: () => void;
}

export type SceneTreeRendererInputDeps = Omit<SceneTreeRendererDeps, 'renderSceneTree'>;

export interface SceneTreeViewportHit {
  pickedNodeId: SceneNodeId | null;
  ownerComponentId: string | null;
  pickTarget: ViewportTarget | null;
  mode: ViewportMode;
}

export interface SceneTreeRendererController {
  render(): void;
  /**
   * Handle a single click in the preview (Figma-style drill): select the node
   * under the cursor; click the already-selected component again to enter it;
   * use a true viewport miss/root hit while inside a component to step back out.
   * Filled background layers remain normal selectable nodes.
   */
  handleViewportClick(hit: SceneTreeViewportHit): void;
  selectSceneFavorite(target: SceneFavoriteTarget): void;
}

export function createSceneTreeRenderer(deps: SceneTreeRendererInputDeps): SceneTreeRendererController {
  const actionDeps = (): SceneTreeRendererDeps => ({ ...deps, renderSceneTree: controller.render });

  const controller: SceneTreeRendererController = {
    render(): void {
      renderSceneTree(actionDeps(), controller.render);
    },
    handleViewportClick(hit: SceneTreeViewportHit): void {
      const appState = deps.appState;
      let pickedNodeId = hit.pickedNodeId;
      let ownerComponentId = hit.ownerComponentId;
      let scene = getOwnerSceneFor(appState, ownerComponentId ?? undefined) ?? getActiveScene(appState);

      if (
        pickedNodeId
        && hit.pickTarget?.type === 'layer'
        && hit.pickTarget.sceneNodeId
        && !scene.nodes[pickedNodeId]
      ) {
        pickedNodeId = hit.pickTarget.sceneNodeId;
        ownerComponentId = hit.pickTarget.componentId ?? null;
        scene = getOwnerSceneFor(appState, ownerComponentId ?? undefined) ?? scene;
      }

      const selectedId = deps.selection.getSelectedSceneNodeId();
      const selectedComponentId = deps.selection.getSelectedSceneNodeComponentId();
      const pickedNode = pickedNodeId ? scene.nodes[pickedNodeId] : null;
      const policy = resolveViewportClickPolicy({
        mode: hit.mode,
        pickedNodeId,
        ownerComponentId,
        activeComponentId: appState.activeComponentId,
        selectedSceneNodeId: selectedId,
        selectedSceneNodeComponentId: selectedComponentId,
        rootIds: scene.rootIds,
        pickedSelectedInstance: pickedNode?.kind === 'instance',
      });

      // Drill in: clicking the already-selected component enters it.
      if (policy.drillIntoPickedInstance && pickedNode?.kind === 'instance') {
        switchToComponent(actionDeps(), pickedNode.componentId);
        return;
      }

      // Only a miss or the rendered active component root steps back out. Stack
      // backgrounds/fill layers are still real layer hits and remain selectable.
      if (policy.stepOutToScene) {
        returnToScene(actionDeps());
        return;
      }

      if (policy.syncOwnerContext) {
        if (ownerComponentId) {
          switchToComponent(actionDeps(), ownerComponentId);
        } else if (appState.activeComponentId) {
          returnToScene(actionDeps());
        }
      }

      if (pickedNodeId && !scene.nodes[pickedNodeId]) return;
      deps.selection.selectSceneNode(pickedNodeId, ownerComponentId);
      controller.render();
    },
    selectSceneFavorite(target: SceneFavoriteTarget): void {
      if (!isSceneFavoriteTargetValid(deps.appState, target)) return;
      if (prepareViewportForSceneFavoriteNavigation(deps.appState)) {
        deps.syncViewportToolbar?.();
        deps.scheduleRender();
      }
      if (target.type === 'scene') {
        if (deps.appState.activeComponentId) {
          returnToScene(actionDeps());
        } else {
          deps.selection.selectVirtualTarget('scene');
          controller.render();
        }
        return;
      }
      if (target.type === 'component') {
        switchToComponent(actionDeps(), target.componentId);
        return;
      }
      if (target.componentId) {
        switchToComponent(actionDeps(), target.componentId);
        deps.selection.selectSceneNode(target.nodeId, target.componentId);
      } else {
        if (deps.appState.activeComponentId) returnToScene(actionDeps());
        deps.selection.selectSceneNode(target.nodeId, null);
      }
      controller.render();
    },
  };

  subscribeEditorTargetSynced(controller.render);
  return controller;
}

function renderSceneTree(deps: SceneTreeRendererDeps, requestRender: () => void): void {
  const list = document.getElementById('scene-tree-list');
  const empty = document.getElementById('scene-tree-empty');
  if (!list) return;

  const appState = deps.appState;
  const inComponentMode = appState.activeComponentId !== null;
  const activeScene = getActiveScene(appState);
  const hasScene = activeScene.rootIds.length > 0;
  const selectedVirtualTarget = deps.selection.getSelectedVirtualTarget();
  let selectedSceneNodeId = deps.selection.getSelectedSceneNodeId();
  const selectedSceneNodeComponentId = deps.selection.getSelectedSceneNodeComponentId();

  if (
    hasScene
    && selectedVirtualTarget === null
    && (
      selectedSceneNodeComponentId !== appState.activeComponentId
      || !selectedSceneNodeId
      || !activeScene.nodes[selectedSceneNodeId]
    )
  ) {
    selectedSceneNodeId = inComponentMode
      ? (activeScene.rootIds[0] ?? null)
      : (findInitialSelectionForActiveComponent(appState.scene, appState.activeComponentId)
          ?? appState.scene.rootIds[0]
          ?? null);
    deps.selection.setSelectedSceneNodeId(selectedSceneNodeId, appState.activeComponentId);
  }

  if (empty) empty.style.display = (hasScene || inComponentMode) ? 'none' : '';
  list.innerHTML = '';

  if (inComponentMode) {
    renderComponentBreadcrumb(deps, requestRender, list);
  }

  if (!hasScene && !inComponentMode) {
    renderSceneTreeToolbar(list, null, false, appState, activeScene, deps);
    renderPropertiesInspector(deps, activeScene);
    return;
  }

  const effectiveSelectionNodeId = selectedVirtualTarget === null
    ? selectedSceneNodeId
    : (activeScene.rootIds[0] ?? null);
  const activeContainerId = getActiveContainerId(activeScene, effectiveSelectionNodeId);

  renderSceneTreeToolbar(list, activeContainerId, inComponentMode, appState, activeScene, deps);

  if (inComponentMode) {
    renderActiveComponentRow(deps, requestRender, list, activeScene);
  } else {
    renderGlobalSceneRow(deps, requestRender, list, activeScene);
  }

  for (const rootId of activeScene.rootIds) {
    renderTreeNode(deps, requestRender, list, activeScene, rootId, 1, activeContainerId,
      inComponentMode ? appState.activeComponentId! : undefined);
  }

  renderPropertiesInspector(deps, activeScene);
}

function renderComponentBreadcrumb(deps: SceneTreeRendererDeps, _requestRender: () => void, parent: HTMLElement): void {
  const appState = deps.appState;
  const comp = appState.activeComponentId ? appState.componentLibrary[appState.activeComponentId] : null;
  const bar = document.createElement('div');
  bar.className = 'scene-tree-breadcrumb';

  const backBtn = document.createElement('button');
  backBtn.className = 'scene-tree-btn';
  backBtn.textContent = '← Scene';
  backBtn.title = 'Back to outer scene';
  backBtn.addEventListener('click', () => returnToScene(deps));
  bar.appendChild(backBtn);

  const label = document.createElement('span');
  label.className = 'scene-tree-breadcrumb-label';
  label.textContent = comp?.name ?? 'Component';
  bar.appendChild(label);

  parent.appendChild(bar);
}

function renderActiveComponentRow(
  deps: SceneTreeRendererDeps,
  requestRender: () => void,
  parent: HTMLElement,
  _activeScene: Scene,
): void {
  const appState = deps.appState;
  const componentId = appState.activeComponentId;
  if (!componentId) return;
  const component = appState.componentLibrary[componentId];
  if (!component) return;

  const row = document.createElement('div');
  row.className = 'scene-tree-item';
  if (deps.selection.getSelectedVirtualTarget() === 'component') row.classList.add('scene-tree-selected');
  row.style.paddingLeft = '8px';
  row.style.cursor = 'pointer';

  const icon = document.createElement('span');
  icon.className = 'scene-tree-icon';
  icon.textContent = '◆';
  row.appendChild(icon);

  const label = document.createElement('span');
  label.className = 'scene-tree-label';
  label.textContent = component.name;
  row.appendChild(label);

  const badge = document.createElement('span');
  badge.className = 'scene-tree-badge';
  badge.textContent = 'component';
  row.appendChild(badge);
  const favoriteTarget = sceneFavoriteTargetForVirtual('component', componentId);
  if (favoriteTarget) row.appendChild(renderFavoriteButton(deps, requestRender, favoriteTarget));

  row.addEventListener('click', () => {
    deps.selection.selectVirtualTarget('component');
    requestRender();
  });

  parent.appendChild(row);
}

function renderGlobalSceneRow(deps: SceneTreeRendererDeps, requestRender: () => void, parent: HTMLElement, _activeScene: Scene): void {
  const row = document.createElement('div');
  row.className = 'scene-tree-item';
  if (deps.selection.getSelectedVirtualTarget() === 'scene') row.classList.add('scene-tree-selected');
  row.style.paddingLeft = '8px';
  row.style.cursor = 'pointer';

  const icon = document.createElement('span');
  icon.className = 'scene-tree-icon';
  icon.textContent = '◈';
  row.appendChild(icon);

  const label = document.createElement('span');
  label.className = 'scene-tree-label';
  label.textContent = 'Scene';
  row.appendChild(label);

  const badge = document.createElement('span');
  badge.className = 'scene-tree-badge';
  badge.textContent = 'root';
  row.appendChild(badge);
  const favoriteTarget = sceneFavoriteTargetForVirtual('scene');
  if (favoriteTarget) row.appendChild(renderFavoriteButton(deps, requestRender, favoriteTarget));

  row.addEventListener('click', () => {
    deps.selection.selectVirtualTarget('scene');
    requestRender();
  });

  parent.appendChild(row);
}

function renderTreeNode(
  deps: SceneTreeRendererDeps,
  requestRender: () => void,
  parent: HTMLElement,
  scene: Scene,
  nodeId: SceneNodeId,
  depth: number,
  activeContainerId: SceneNodeId | null,
  componentId?: string,
): void {
  const node = scene.nodes[nodeId];
  if (!node) return;

  const selectedVirtualTarget = deps.selection.getSelectedVirtualTarget();
  const selectedSceneNodeId = deps.selection.getSelectedSceneNodeId();
  const selectedSceneNodeComponentId = deps.selection.getSelectedSceneNodeComponentId();
  const nodeComponentId = componentId ?? null;
  const isSelected = selectedVirtualTarget === null
    && nodeId === selectedSceneNodeId
    && selectedSceneNodeComponentId === nodeComponentId;
  const isActiveContainer = node.kind === 'frame' && nodeId === activeContainerId;

  const row = document.createElement('div');
  row.className = 'scene-tree-item';
  if (isSelected) row.classList.add('scene-tree-selected');
  row.style.paddingLeft = `${8 + depth * 14}px`;

  if (node.kind === 'frame') {
    const isCollapsed = deps.selection.isFrameCollapsed(nodeId);
    row.classList.add('scene-tree-frame');
    row.style.cursor = 'pointer';

    const icon = document.createElement('span');
    icon.className = 'scene-tree-icon scene-tree-toggle';
    if (isActiveContainer) icon.classList.add('scene-tree-parent-marker');
    icon.textContent = isCollapsed ? '▸' : '▾';
    icon.addEventListener('click', (e) => {
      e.stopPropagation();
      deps.selection.toggleFrameCollapsed(nodeId);
      requestRender();
    });
    row.appendChild(icon);

    const label = document.createElement('span');
    label.className = 'scene-tree-label';
    label.textContent = node.name;
    row.appendChild(label);

    const nodeDirection = node.layout.kind === 'free' ? null : node.layout.kind;
    if (nodeDirection) {
      const badge = document.createElement('span');
      badge.className = 'scene-tree-badge';
      badge.textContent = nodeDirection;
      row.appendChild(badge);
    }
    row.appendChild(renderFavoriteButton(deps, requestRender, sceneFavoriteTargetForNode(nodeComponentId, nodeId)));

    row.addEventListener('click', () => {
      deps.selection.selectSceneNode(nodeId, nodeComponentId);
      requestRender();
    });

    parent.appendChild(row);

    if (!isCollapsed) {
      for (const childId of node.childIds) {
        renderTreeNode(deps, requestRender, parent, scene, childId, depth + 1, activeContainerId, componentId);
      }
    }
  } else if (node.kind === 'layer') {
    const layerNode = node as LayerNode;
    row.classList.add('scene-tree-layer');

    const icon = document.createElement('span');
    icon.className = 'scene-tree-icon';
    icon.textContent = '●';
    row.appendChild(icon);

    const label = document.createElement('span');
    label.className = 'scene-tree-label';
    label.textContent = layerNode.name;
    row.appendChild(label);

    const badge = document.createElement('span');
    badge.className = 'scene-tree-badge';
    badge.textContent = 'layer';
    row.appendChild(badge);
    row.appendChild(renderFavoriteButton(deps, requestRender, sceneFavoriteTargetForNode(nodeComponentId, nodeId)));

    row.style.cursor = 'pointer';
    row.addEventListener('click', () => {
      deps.selection.selectSceneNode(nodeId, nodeComponentId);
      requestRender();
    });

    parent.appendChild(row);
  } else if (node.kind === 'instance') {
    const isActive = node.componentId === deps.appState.activeComponentId;
    if (isActive) row.classList.add('scene-tree-active');

    const icon = document.createElement('span');
    icon.className = 'scene-tree-icon';
    icon.textContent = '◆';
    row.appendChild(icon);

    const label = document.createElement('span');
    label.className = 'scene-tree-label';
    label.textContent = node.name;
    row.appendChild(label);

    const badge = document.createElement('button');
    badge.type = 'button';
    badge.className = 'scene-tree-badge scene-tree-badge-button';
    badge.textContent = 'component';
    badge.title = isActive ? 'Open component' : 'Enter component';
    badge.setAttribute('aria-label', badge.title);
    badge.addEventListener('click', (e) => {
      e.stopPropagation();
      deps.selection.selectSceneNode(nodeId, nodeComponentId);
      switchToComponent(deps, node.componentId);
    });
    row.appendChild(badge);
    row.appendChild(renderFavoriteButton(deps, requestRender, sceneFavoriteTargetForNode(nodeComponentId, nodeId)));

    row.addEventListener('click', () => {
      deps.selection.selectSceneNode(nodeId, nodeComponentId);
      requestRender();
    });
    row.addEventListener('dblclick', () => {
      deps.selection.selectSceneNode(nodeId, nodeComponentId);
      switchToComponent(deps, node.componentId);
    });
    row.style.cursor = 'pointer';

    parent.appendChild(row);
  }
}

function renderFavoriteButton(
  deps: SceneTreeRendererDeps,
  requestRender: () => void,
  target: SceneFavoriteTarget,
): HTMLButtonElement {
  const button = document.createElement('button');
  const saved = isSceneFavoriteTargetSaved(deps.appState, target);
  button.type = 'button';
  button.className = 'scene-tree-favorite';
  if (saved) button.classList.add('is-saved');
  button.title = saved ? 'Remove from favorites' : 'Add to favorites';
  button.setAttribute('aria-label', button.title);
  button.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 4.5 2.15 4.35 4.8.7-3.48 3.4.82 4.78L12 15.47 7.71 17.73l.82-4.78-3.48-3.4 4.8-.7Z"/></svg>';
  button.addEventListener('click', e => {
    e.stopPropagation();
    toggleSceneFavorite(deps.appState, target);
    requestRender();
    deps.onFavoritesChanged?.();
  });
  return button;
}
